import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withModifiers as _withModifiers, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "app-custom-textarea-wrapper" }
const _hoisted_2 = ["value", "placeholder", "disabled", "rows"]

import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from 'vue'
import AppIcon from '@/components/ui-kit/icon/AppIcon.vue'
import AppTooltip from '@/components/ui-kit/tooltip/AppTooltip.vue'

type NewLineMode = 'enter' | 'ctrl-enter' | 'shift-enter' | 'none'

type AppCustomTextareaProps = {
	modelValue?: string
	placeholder?: string
	minHeight?: number
	maxHeight?: number
	minRows?: number
	maxRows?: number
	disabled?: boolean
	autoFocus?: boolean
	newLineMode?: NewLineMode
	showClearButton?: boolean
	clearHintText?: string
}

type AppCustomTextareaEmits = {
	(e: 'update:modelValue', value: string): void
	(e: 'focus'): void
	(e: 'blur'): void
	(e: 'change', value: string): void
	(e: 'enter'): void
	(e: 'ctrl-enter'): void
	(e: 'shift-enter'): void
	(e: 'clear', value: string): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppCustomTextarea',
  props: {
    modelValue: { default: '' },
    placeholder: { default: '' },
    minHeight: { default: 32 },
    maxHeight: { default: 0 },
    minRows: { default: 1 },
    maxRows: { default: 0 },
    disabled: { type: Boolean, default: false },
    autoFocus: { type: Boolean, default: false },
    newLineMode: { default: 'enter' },
    showClearButton: { type: Boolean, default: true },
    clearHintText: {}
  },
  emits: ["update:modelValue", "focus", "blur", "change", "enter", "ctrl-enter", "shift-enter", "clear"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

const textareaRef = ref<HTMLTextAreaElement | null>(null)
const currentRows = ref(props.minRows)
const hasScrollbar = ref(false)

const hasValue = computed(() => props.modelValue.length > 0)

const textareaStyle = computed(() => {
	const styles: Record<string, string> = {
		minHeight: `${props.minHeight}px`
	}
	if (props.maxHeight) {
		styles.maxHeight = `${props.maxHeight}px`
	}
	styles.overflowY = props.maxRows > 0
		? (hasScrollbar.value ? 'auto' : 'hidden')
		: 'auto'
	// Освобождаем место справа под иконку очистки, чтобы текст не уходил под неё
	if (props.showClearButton && hasValue.value && !props.disabled) {
		styles.paddingRight = '32px'
	}
	return styles
})

const resize = () => {
	const el = textareaRef.value
	if (!el) return

	el.style.removeProperty('height')

	if (props.maxRows <= 0) {
		el.style.height = `${props.minHeight}px`
		const style = window.getComputedStyle(el)
		const borderY =
			parseFloat(style.borderTopWidth || '0') +
			parseFloat(style.borderBottomWidth || '0')

		el.style.height = `${Math.max(props.minHeight, el.scrollHeight + borderY)}px`
		hasScrollbar.value = false
		return
	}

	el.rows = props.minRows
	const style = window.getComputedStyle(el)
	const lineHeight = parseInt(style.lineHeight) || 18
	const paddingTop = parseInt(style.paddingTop) || 0
	const paddingBottom = parseInt(style.paddingBottom) || 0
	const contentHeight = el.scrollHeight - paddingTop - paddingBottom
	const calculatedRows = Math.floor(contentHeight / lineHeight)
	const rows = Math.min(
		Math.max(calculatedRows, props.minRows),
		props.maxRows
	)

	el.rows = rows
	currentRows.value = rows
	hasScrollbar.value = calculatedRows > props.maxRows
}

const handleInput = (event: Event) => {
	const value = (event.target as HTMLTextAreaElement).value
	emit('update:modelValue', value)
	emit('change', value)
	nextTick(resize)
}

const handleKeydown = (event: KeyboardEvent) => {
	if (event.key !== 'Enter') return
	const { ctrlKey, shiftKey, metaKey } = event

	if (props.newLineMode === 'enter') {
		if (ctrlKey || metaKey) {
			event.preventDefault()
			emit('ctrl-enter')
		} else if (shiftKey) {
			emit('shift-enter')
		}
	} else if (props.newLineMode === 'shift-enter') {
		if (shiftKey) {
			emit('shift-enter')
		} else if (ctrlKey || metaKey) {
			event.preventDefault()
			emit('ctrl-enter')
		} else {
			event.preventDefault()
			emit('enter')
		}
	} else if (props.newLineMode === 'ctrl-enter') {
		if (ctrlKey || metaKey) {
			emit('ctrl-enter')
		} else if (shiftKey) {
			event.preventDefault()
			emit('shift-enter')
		} else {
			event.preventDefault()
			emit('enter')
		}
	} else if (props.newLineMode === 'none') {
		if (ctrlKey || metaKey) {
			event.preventDefault()
			emit('ctrl-enter')
		} else if (shiftKey) {
			event.preventDefault()
			emit('shift-enter')
		} else {
			event.preventDefault()
			emit('enter')
		}
	}
}

const clearField = () => {
	if (props.disabled) return

	emit('update:modelValue', '')
	emit('change', '')
	emit('clear', '')
	nextTick(() => {
		resize()
		textareaRef.value?.focus()
	})
}

watch(
	() => [props.modelValue, props.minRows, props.maxRows],
	() => nextTick(resize)
)

onMounted(() => {
	resize()
	if (props.autoFocus && !props.disabled) {
		nextTick(() => textareaRef.value?.focus())
	}
})

__expose({
	focus: () => textareaRef.value?.focus(),
	blur: () => textareaRef.value?.blur(),
	reset: () => {
		if (props.modelValue !== '') {
			emit('update:modelValue', '')
			emit('change', '')
		}
		nextTick(resize)
	},
	getValue: () => textareaRef.value?.value ?? ''
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("textarea", {
      ref_key: "textareaRef",
      ref: textareaRef,
      class: _normalizeClass(["app-custom-textarea", { 'app-custom-textarea_disabled': _ctx.disabled }]),
      value: _ctx.modelValue,
      placeholder: _ctx.placeholder || _ctx.$t('ui_enter_text'),
      disabled: _ctx.disabled,
      rows: _ctx.minRows,
      style: _normalizeStyle(textareaStyle.value),
      onInput: handleInput,
      onKeydown: handleKeydown,
      onFocus: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('focus'))),
      onBlur: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('blur')))
    }, null, 46, _hoisted_2),
    (_ctx.showClearButton && hasValue.value && !_ctx.disabled)
      ? (_openBlock(), _createBlock(AppTooltip, {
          key: 0,
          class: "app-custom-textarea__clear",
          text: _ctx.clearHintText || _ctx.$t('clear_field')
        }, {
          activator: _withCtx(() => [
            _createVNode(AppIcon, {
              name: "arrow-left",
              width: "16px",
              color: "#d2d2d2",
              cursor: "pointer",
              onClick: clearField,
              onMousedown: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent"]))
            })
          ]),
          _: 1
        }, 8, ["text"]))
      : _createCommentVNode("", true)
  ]))
}
}

})